import { t } from '@/lib/i18n';
import { createFileRoute } from '@tanstack/react-router';
import { PageContainer, ProCard } from '@ant-design/pro-components';
import { Button, Radio, Segmented, Space, Switch, Typography } from 'antd';
import { useDocumentTitle } from '@/hooks/use-document-title';
import {
  layoutOptions,
  primaryColorOptions,
  useAppSettings,
} from '@/theme/app-settings';
import type { LayoutMode, ThemeMode } from '@/theme/app-settings';

export const Route = createFileRoute('/_authenticated/settings')({
  component: SettingsPage,
});

/**
 * Appearance settings.
 *
 * They live in a React context backed by `localStorage`, not on the server: this is one
 * operator's preference for one browser, and putting it behind an API would make a personal
 * choice into shared state. antd's algorithms do the actual theming — see
 * `src/theme/theme-provider.tsx` — so nothing here recomputes colours by hand.
 */
function SettingsPage() {
  useDocumentTitle(t('系统设置'));
  const { settings, update, reset } = useAppSettings();

  return (
    <PageContainer header={{ title: t('系统设置') }}>
      <Space orientation="vertical" size={16} className="flex">
        <ProCard
          title={t('外观')}
          variant="outlined"
          extra={
            <Button size="small" onClick={reset}>
              {t('恢复默认')}
            </Button>
          }
        >
          <Space orientation="vertical" size={20} className="flex">
            <div>
              <Typography.Text strong>{t('主题模式')}</Typography.Text>
              <div className="mt-2">
                <Segmented
                  value={settings.themeMode}
                  onChange={(value) =>
                    update({ themeMode: value as ThemeMode })
                  }
                  options={[
                    { label: t('浅色'), value: 'light' },
                    { label: t('深色'), value: 'dark' },
                  ]}
                />
              </div>
            </div>

            <div>
              <Typography.Text strong>{t('主色')}</Typography.Text>
              <div className="mt-2">
                <Radio.Group
                  value={settings.primaryColor}
                  onChange={(event) =>
                    update({ primaryColor: event.target.value as string })
                  }
                >
                  <Space wrap size={12}>
                    {primaryColorOptions.map((option) => (
                      <Radio.Button key={option.value} value={option.value}>
                        <Space size={8}>
                          <span
                            className={`inline-block size-3.5 rounded ${option.swatchClassName}`}
                          />
                          {option.label}
                        </Space>
                      </Radio.Button>
                    ))}
                  </Space>
                </Radio.Group>
              </div>
            </div>

            <div>
              <Typography.Text strong>{t('整体布局')}</Typography.Text>
              <div className="mt-2">
                <Radio.Group
                  value={settings.layout}
                  onChange={(event) =>
                    update({ layout: event.target.value as LayoutMode })
                  }
                  options={layoutOptions.map((option) => ({ ...option }))}
                  optionType="button"
                />
              </div>
            </div>

            <div>
              <Space size={12}>
                <Typography.Text strong>{t('紧凑模式')}</Typography.Text>
                <Switch
                  checked={settings.compact}
                  onChange={(checked) => update({ compact: checked })}
                />
              </Space>
            </div>
          </Space>
        </ProCard>
      </Space>
    </PageContainer>
  );
}
